import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { type ReactNode, useEffect } from 'react';

import { palette } from '../../theme';

export type ModalBackdropProps = {
    onClose: () => void;
    ariaLabel: string;
    closeOnEscape?: boolean;
    closeOnBackdropClick?: boolean;
    zIndex?: number;
    align?: 'center' | 'right';
    children: ReactNode;
};

export const ModalBackdrop: React.FC<ModalBackdropProps> = ({
    onClose,
    ariaLabel,
    closeOnEscape = true,
    closeOnBackdropClick = true,
    zIndex = 1000,
    align = 'center',
    children,
}) => {
    const classes = useStyles();

    useEffect(() => {
        if (!closeOnEscape) {
            return;
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [closeOnEscape, onClose]);

    return (
        <div
            className={mergeClasses(classes.backdrop, align === 'right' && classes.alignRight)}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            style={{ zIndex }}
            onClick={(e) => {
                if (closeOnBackdropClick && e.target === e.currentTarget) {
                    onClose();
                }
            }}
            onKeyDown={(e) => {
                if (closeOnEscape && e.key === 'Escape') {
                    onClose();
                }
            }}
        >
            {children}
        </div>
    );
};

const useStyles = makeStyles({
    backdrop: {
        position: 'fixed',
        inset: 0,
        backgroundColor: palette.backdrop,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    alignRight: {
        alignItems: 'stretch',
        justifyContent: 'flex-end',
    },
});
